import { advanceWet, dryGround, type WetState } from "odyssey-shared";
import type { PuddleFrame } from "./puddle-view.ts";
import { WeatherFade } from "./weather-fade.ts";

const DRY: WetState = dryGround();
const RESEED_AFTER_MS = 60_000;

/**
 * Where the ground's state is read from: the region's state at a time, and whether a development page holds it still.
 */
export interface GroundSource {
  readonly wetStart?: (epochMs: number) => WetState;
  readonly wetFrozen?: boolean;
}

/**
 * The wet ground the weather view draws, and the strength it draws: stepped by real minutes of the region's weather, read
 * afresh from the clock when the page opens or was away, held dry before the shared time is known, and during a seam
 * crossing's fade (ADR-147 note 15) eased from the state shown to the new region's on the fade's own clock. Writes into
 * objects made once, so a frame allocates nothing.
 */
export class WeatherGround {
  public readonly state: WetState = dryGround();
  private readonly fade = new WeatherFade();
  private wetClock = -1;
  private drawn = 0;

  /**
   * @param source The region's ground, read each time it is needed, so a crossing's new region is read once it stands.
   * @param fadeNow The monotonic clock the fade runs on.
   */
  public constructor(
    private readonly source: () => GroundSource,
    private readonly fadeNow: () => number,
  ) {}

  /**
   * The strength drawn in the last frame outdoors.
   *
   * @returns The strength.
   */
  public get strength(): number {
    return this.drawn;
  }

  /**
   * Whether a crossing's fade is running.
   *
   * @returns True while it eases.
   */
  public get fading(): boolean {
    return this.fade.active;
  }

  /**
   * Begins a crossing's fade from what is drawn now to the new region's ground, or switches at once.
   *
   * @param clock The shared time, or null before it is known.
   * @param fadeMs How long the fade runs; 0 switches at once.
   */
  public stand(clock: number | null, fadeMs: number): void {
    if (clock === null || fadeMs <= 0) {
      this.fade.end();
      this.wetClock = -1;
      return;
    }
    this.fade.begin(this.fadeNow(), fadeMs, this.drawn, this.state, this.source().wetStart?.(clock) ?? DRY);
  }

  /**
   * Moves the ground to the time and gives the strength to draw outdoors.
   *
   * @param clock The shared time, or null before it is known.
   * @param target The region's strength now.
   * @returns The strength drawn: the region's, or the eased one during a fade; 0 before the time is known.
   */
  public step(clock: number | null, target: number): number {
    let outdoors = target;
    if (clock === null) {
      this.forget();
      outdoors = 0;
    } else if (this.fade.active) {
      const fadeAt = this.fadeNow();
      outdoors = this.fade.strength(fadeAt, target);
      if (this.fade.done(fadeAt)) {
        this.fade.end();
        this.seed(clock);
      } else {
        this.fade.groundInto(fadeAt, this.state);
      }
      this.wetClock = clock;
    } else {
      this.stepWet(clock, target);
    }
    this.drawn = outdoors;
    return outdoors;
  }

  /**
   * Writes what the puddles read: the ground, and during a fade the ground they fade from and how far the fade has run.
   *
   * @param frame The puddle frame written.
   */
  public puddlesInto(frame: { -readonly [K in keyof PuddleFrame]: PuddleFrame[K] }): void {
    const fading = this.fade.active;
    frame.ground = fading ? this.fade.to : this.state;
    frame.from = fading ? this.fade.from : null;
    frame.mix = fading ? this.fade.mix(this.fadeNow()) : 1;
  }

  /**
   * Steps the wet minutes from the last frame's time, or reads them afresh when the page has just opened or was away a minute
   * or more; a held ground is read once and kept.
   *
   * @param now The shared time in milliseconds.
   * @param strength The region's strength.
   */
  private stepWet(now: number, strength: number): void {
    if (this.source().wetFrozen === true) {
      if (this.wetClock < 0) this.seed(now);
      this.wetClock = now;
      return;
    }
    if (this.wetClock < 0 || now - this.wetClock > RESEED_AFTER_MS || now < this.wetClock) {
      this.seed(now);
    } else {
      advanceWet(this.state, strength, now - this.wetClock);
    }
    this.wetClock = now;
  }

  /**
   * Holds the ground dry before the shared time is known; it is read from the clock when the time arrives.
   */
  private forget(): void {
    const ground = this.state;
    ground.small = 0;
    ground.medium = 0;
    ground.large = 0;
    ground.dry = 0;
    ground.soak = 0;
    this.wetClock = -1;
  }

  /**
   * Reads the ground afresh from the region at a time.
   *
   * @param now The shared time in milliseconds.
   */
  private seed(now: number): void {
    Object.assign(this.state, this.source().wetStart?.(now) ?? DRY);
  }
}
